import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { MediaTypeBadges, StatusBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Lock, ExternalLink, ShoppingBag, Clapperboard, ArrowRight } from "lucide-react";

const ORDER_STATUS_CLS = {
  on_hold: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  released: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-zinc-800 text-zinc-500 border-zinc-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [media, setMedia] = useState(null);
  const [orders, setOrders] = useState([]);
  const [unlock, setUnlock] = useState(null); // {media, link}
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/my/media").then(({ data }) => setMedia(data)).catch(() => setMedia([]));
    api.get("/my/orders").then(({ data }) => setOrders(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <Navbar />
      <Breadcrumb label="Back to store" to="/" />
      <div className="mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Your portal</div>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
              Welcome, {user?.name}
            </h1>
          </div>
          <Button
            data-testid="browse-new-media"
            onClick={() => navigate("/")}
            className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]"
          >
            <Clapperboard className="mr-2 h-4 w-4" /> Browse new media <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>

        {/* Recent media */}
        <section>
          <h2 className="mb-5 font-display text-xl font-bold uppercase tracking-tight text-white">Your media library</h2>
          {media === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#ff2a5f]" /></div>
          ) : media.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-16 text-center text-zinc-500">
              No media yet. Purchases and shared media will appear here.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {media.map((m) => (
                <div key={m.grant_id} data-testid={`library-item-${m.id}`} className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/70">
                  <div className="relative aspect-[16/10] bg-zinc-950">
                    {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className="h-full w-full object-cover" />}
                    <div className="absolute left-3 top-3"><StatusBadge status={m.order_status} /></div>
                  </div>
                  <div className="space-y-3 p-5">
                    <MediaTypeBadges types={m.media_types} testidPrefix={`lib-badge-${m.id}`} />
                    <h3 className="font-display text-lg font-bold text-white">{m.title}</h3>
                    <div className="space-y-2 pt-1">
                      {!m.available ? (
                        <p className="text-sm text-zinc-500">Not currently available.</p>
                      ) : m.links.length === 0 ? (
                        <p className="text-sm text-zinc-500">Links coming soon.</p>
                      ) : (
                        m.links.map((l) => (
                          <button
                            key={l.id}
                            data-testid={`unlock-link-${m.id}-${l.id}`}
                            onClick={() => setUnlock({ media: m, link: l })}
                            className="flex w-full items-center justify-between rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-left text-sm text-zinc-200 hover:border-rose-500/50"
                          >
                            <span className="flex items-center gap-2">
                              {l.has_password ? <Lock className="h-3.5 w-3.5 text-amber-400" /> : <ExternalLink className="h-3.5 w-3.5 text-cyan-400" />}
                              {l.label}
                            </span>
                            <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Orders */}
        <section>
          <h2 className="mb-5 flex items-center gap-2 font-display text-xl font-bold uppercase tracking-tight text-white">
            <ShoppingBag className="h-5 w-5 text-[#ff2a5f]" /> Your orders
          </h2>
          <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/60 backdrop-blur-md">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Order</TableHead>
                  <TableHead className="text-zinc-400">Item</TableHead>
                  <TableHead className="text-zinc-400">Method</TableHead>
                  <TableHead className="text-zinc-400">Payment</TableHead>
                  <TableHead className="text-zinc-400">Total</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow className="border-zinc-800"><TableCell colSpan={6} className="py-10 text-center text-zinc-500">No orders yet.</TableCell></TableRow>
                ) : (
                  orders.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setDetail(o)} className="cursor-pointer border-zinc-800 hover:bg-zinc-800/40">
                      <TableCell className="font-mono-x text-xs text-zinc-300">{o.order_number}</TableCell>
                      <TableCell className="text-white">{o.items.map((i) => i.title).join(", ")}</TableCell>
                      <TableCell className="capitalize text-zinc-300">{o.payment_method.replace("_", " ")}</TableCell>
                      <TableCell className="capitalize text-zinc-300">{o.payment_status}</TableCell>
                      <TableCell className="text-white">{money(o.total)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${ORDER_STATUS_CLS[o.status] || ORDER_STATUS_CLS.on_hold}`}>
                          {o.status.replace("_", " ")}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>

      <UnlockDialog data={unlock} onClose={() => setUnlock(null)} />
      <OrderDetailDialog order={detail} onClose={() => setDetail(null)} />
      <Footer />
    </div>
  );
}

function OrderDetailDialog({ order, onClose }) {
  if (!order) return null;
  const subtotal = order.subtotal != null ? order.subtotal : order.total;
  const Line = ({ label, value, muted, accent }) => (
    <div className={`flex justify-between ${accent ? "text-emerald-400" : muted ? "text-zinc-500" : "text-zinc-300"}`}><span>{label}</span><span>{value}</span></div>
  );
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display font-mono-x text-sm">Order {order.order_number}</DialogTitle>
          <DialogDescription className="text-zinc-500">Full price breakdown for this order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3" data-testid="order-detail">
          <div className="space-y-2">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-zinc-300">{i.title}</span>
                <span className="text-zinc-400">{money(i.price)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5 border-t border-zinc-800 pt-3 text-sm">
            <Line label="Subtotal" value={money(subtotal)} />
            {order.discount > 0 && <Line label={order.discount_label || "Discount"} value={`- ${money(order.discount)}`} accent />}
            {order.surcharge > 0 && <Line label="Card surcharge" value={money(order.surcharge)} />}
            <Line label="Incl. GST" value={money(order.gst || 0)} muted />
            <div className="flex justify-between pt-2 font-display text-base font-extrabold text-white">
              <span>Total</span><span data-testid="detail-total">{money(order.total)}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnlockDialog({ data, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setPw("");
    setResult(null);
  }, [data]);

  if (!data) return null;
  const { media, link } = data;

  const submit = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/my/media/${media.id}/links/${link.id}/unlock`, { password: pw });
      setResult(res);
      toast.success("Access granted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Lock className="h-5 w-5 text-amber-400" /> {link.label}
          </DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-4" data-testid="unlock-result">
            <p className="text-sm text-zinc-400">Your link is ready:</p>
            <a href={result.url} target="_blank" rel="noreferrer" data-testid="open-media-link">
              <Button className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">
                Open / download <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {link.has_password ? (
              <>
                <p className="text-sm text-zinc-400">This media is password-protected. Enter the access password.</p>
                <Input
                  data-testid="unlock-password-input"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Access password"
                  className="border-zinc-700 bg-zinc-950 text-white"
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                />
              </>
            ) : (
              <p className="text-sm text-zinc-400">Click below to reveal your link.</p>
            )}
            <Button data-testid="unlock-submit" onClick={submit} disabled={busy} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
